// Este arquivo é um wrapper para o hook useRealtimeMediasSync
// para contornar problemas de importação no Vite

import { useEffect, useRef } from "react";
import { supabase } from "../lib/supabase";

/**
 * Hook para sincronização em tempo real de mídias
 */
export const useRealtimeMediasSync = ({
  deviceCode,
  activatedAccount,
  checkActivation,
  isActivated,
  activatedUserId,
  organizationId,
}) => {
  const syncTimeoutRef = useRef(null);
  const isConnectedRef = useRef(false);
  const channelRef = useRef(null);
  const hasInitializedRef = useRef(false);

  useEffect(() => {
    // Evitar inicialização múltipla
    if (hasInitializedRef.current) {
      return;
    }

    if (isActivated && deviceCode && organizationId) {
      hasInitializedRef.current = true;
      
      console.log(`🔄 [REALTIME] Iniciando monitoramento em tempo real`, {
        deviceCode,
        organizationId,
        timestamp: new Date().toISOString()
      });

      // Controle de sincronização para evitar chamadas simultâneas
      let isSyncing = false;
      let pendingSync = false;

      const debouncedSync = (delay = 1000) => {
        if (syncTimeoutRef.current) {
          clearTimeout(syncTimeoutRef.current);
        }
        
        syncTimeoutRef.current = setTimeout(async () => {
          if (isSyncing) {
            pendingSync = true;
            return;
          }

          try {
            isSyncing = true;
            console.log('🔄 [REALTIME] Iniciando sincronização...');
            
            // Verificar se temos todos os dados necessários
            if (!isActivated) {
              console.log('⚠️ [REALTIME] Sincronização ignorada: dispositivo não ativado');
              return;
            }
            
            if (!organizationId) {
              console.log('⚠️ [REALTIME] Sincronização ignorada: organizationId não definido');
              return;
            }
            
            // Emitir evento customizado para o Player buscar mídias
            window.dispatchEvent(new CustomEvent('realtime-sync-requested', {
              detail: { deviceCode, organizationId }
            }));
            
            console.log('✅ [REALTIME] Sincronização concluída');

            // Se houver uma sincronização pendente, executá-la
            if (pendingSync) {
              pendingSync = false;
              window.dispatchEvent(new CustomEvent('realtime-sync-requested', {
                detail: { deviceCode, organizationId }
              }));
            }
          } catch (error) {
            console.error('❌ [REALTIME] Erro na sincronização:', error);
          } finally {
            isSyncing = false;
          }
        }, delay);
      };

      const handleSync = async () => {
        debouncedSync(500); // Debounce de 500ms para mudanças rápidas
      };

      // Limpar canal anterior se existir
      if (channelRef.current) {
        console.log('🔌 [REALTIME] Removendo canal anterior');
        supabase.removeChannel(channelRef.current);
      }

      const channel = supabase
        .channel(`realtime-${deviceCode}-${organizationId}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'medias',
            filter: `organization_id=eq.${organizationId}`,
          },
          async (payload) => {
            const changes = payload.new || payload.old;
            console.log('🔄 [REALTIME] Mudança detectada em medias:', {
              tipo: payload.eventType,
              mediaId: changes?.id,
              timestamp: new Date().toISOString()
            });

            // Se o dispositivo estiver ativado e a mudança for relevante, atualizar as mídias
            if (activatedAccount && changes) {
              await handleSync();
            }
          }
        )
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'devices',
            filter: `code=eq.${deviceCode}`,
          },
          async (payload) => {
            console.log('🔄 [REALTIME] Mudança detectada em devices:', {
              deviceCode,
              tipo: payload.eventType,
              timestamp: new Date().toISOString()
            });
            if (activatedAccount) {
              await checkActivation();
              // Atualizar mídias após verificar ativação
              await handleSync();
            }
          }
        )
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'screens',
            filter: `organization_id=eq.${organizationId}`,
          },
          async (payload) => {
            console.log('🔄 [REALTIME] Mudança detectada em screens:', {
              tipo: payload.eventType,
              screenId: payload.new?.id || payload.old?.id,
              timestamp: new Date().toISOString()
            });
            if (activatedAccount) {
              await Promise.all([checkActivation(), handleSync()]);
            }
          }
        )
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'media_schedules',
            filter: `organization_id=eq.${organizationId}`,
          },
          async (payload) => {
            console.log('🔄 [REALTIME] Mudança detectada em media_schedules:', {
              tipo: payload.eventType,
              scheduleId: payload.new?.id || payload.old?.id,
              timestamp: new Date().toISOString()
            });
            if (activatedAccount) {
              await handleSync();
            }
          }
        )
        .subscribe((status) => {
          isConnectedRef.current = status === 'SUBSCRIBED';
          console.log(`📡 [REALTIME] Status do canal:`, {
            deviceCode,
            organizationId,
            status,
            isConnected: isConnectedRef.current,
            timestamp: new Date().toISOString()
          });
        });

      channelRef.current = channel;

      // Força primeira sincronização ao montar
      if (activatedAccount) {
        console.log('🔄 [REALTIME] Primeira sincronização ao montar...');
        handleSync();
      }

      // Sincronização periódica a cada 5 minutos como fallback
      const periodicSync = setInterval(() => {
        if (activatedAccount && isConnectedRef.current) {
          console.log('🔄 [REALTIME] Sincronização periódica...');
          handleSync();
        }
      }, 5 * 60 * 1000); // 5 minutos

      // Verificação de conectividade a cada 30 segundos
      const connectivityCheck = setInterval(() => {
        if (activatedAccount && !isConnectedRef.current) {
          console.log('🔌 [REALTIME] Conexão perdida, tentando reconectar...');
          // Forçar uma nova sincronização
          handleSync();
        }
      }, 30 * 1000); // 30 segundos

      return () => {
        console.log(`🔌 [REALTIME] Desconectando canal:`, {
          deviceCode,
          organizationId,
          timestamp: new Date().toISOString()
        });
        
        if (syncTimeoutRef.current) {
          clearTimeout(syncTimeoutRef.current);
        }
        
        if (channelRef.current) {
          supabase.removeChannel(channelRef.current);
          channelRef.current = null;
        }
        
        clearInterval(periodicSync);
        clearInterval(connectivityCheck);
        hasInitializedRef.current = false;
      };
    }
  }, [deviceCode, organizationId]); // Removidas dependências que causavam loops
};
